// Banner de instalación de la PWA. Portado de fichador-qr, donde ya está probado
// en iPhone y Android.
//
// Dos modos, porque los dos sistemas instalan distinto:
//   - Android/Chrome dispara `beforeinstallprompt`; lo guardamos y el botón
//     "Instalar" lo dispara cuando el usuario quiere.
//   - iPhone/Safari no tiene API de instalación: el banner muestra la guía
//     "Compartir → Agregar a Inicio" y al tocarlo se abre el paso a paso.
//
// No aparece si la app ya corre instalada, y la X lo oculta por 10 días.

let installPromptEvent = null;
let toastEl = null;

const STORAGE_KEY = "pwa_install_dismissed_until";
const DISMISS_DAYS = 10;
const NombreApp = "Claves Celular";

export function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

function isIOSStandaloneCapable() {
  return isIOS() && isSafari();
}

function getDismissedUntil() {
  const v = localStorage.getItem(STORAGE_KEY);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function dismissForDays(days = DISMISS_DAYS) {
  const until = Date.now() + days * 24 * 60 * 60 * 1000;
  localStorage.setItem(STORAGE_KEY, String(until));
}

function isDismissed() {
  return Date.now() < getDismissedUntil();
}

function removeToast() {
  if (!toastEl) return;
  toastEl.remove();
  toastEl = null;
}

function hideToast() {
  if (!toastEl) return;
  toastEl.classList.remove("pwa-toast-show");
  toastEl.classList.add("pwa-toast-hide");
  setTimeout(() => {
    if (toastEl) toastEl.style.display = "none";
  }, 220);
}

function showToast() {
  if (!toastEl) createToast();
  if (isStandalone()) return;
  if (isDismissed()) return;

  const canShowIOSGuide = isIOSStandaloneCapable();
  const canShowInstallPrompt = !!installPromptEvent;

  if (!canShowIOSGuide && !canShowInstallPrompt) return;

  setToastMode(canShowIOSGuide ? "ios" : "install");
  toastEl.style.display = "flex";
  toastEl.classList.remove("pwa-toast-hide");
  toastEl.getBoundingClientRect(); // fuerza reflow para reiniciar la animación
  toastEl.classList.add("pwa-toast-show");
}

function setToastMode(mode) {
  const subtitle = toastEl.querySelector("[data-subtitle]");
  const installBtn = toastEl.querySelector("[data-install-btn]");

  if (mode === "ios") {
    subtitle.textContent = "Compartir → Agregar a Inicio. Tocá acá para ver cómo.";
    installBtn.style.display = "none";
  } else {
    subtitle.textContent = "Acceso rápido desde tu pantalla de inicio.";
    installBtn.style.display = "inline-flex";
  }
}

function createToast() {
  if (toastEl) return toastEl;

  toastEl = document.createElement("div");
  toastEl.id = "pwa-install-toast";

  const content = document.createElement("div");
  content.className = "pwa-content";

  const title = document.createElement("div");
  title.className = "pwa-title";
  title.textContent = "Instalá " + NombreApp;

  const subtitle = document.createElement("div");
  subtitle.className = "pwa-subtitle";
  subtitle.setAttribute("data-subtitle", "1");

  content.appendChild(title);
  content.appendChild(subtitle);

  const actions = document.createElement("div");
  actions.className = "pwa-actions";

  const installBtn = document.createElement("button");
  installBtn.type = "button";
  installBtn.className = "pwa-install-btn";
  installBtn.setAttribute("data-install-btn", "1");
  installBtn.textContent = "Instalar";

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "pwa-close-btn";
  closeBtn.setAttribute("aria-label", "Cerrar");
  closeBtn.textContent = "×";

  installBtn.addEventListener("click", async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;
    if (choice.outcome !== "accepted") dismissForDays();
    hideToast();
    installPromptEvent = null;
  });

  // En iPhone no hay botón: el texto del toast abre la guía.
  content.addEventListener("click", () => {
    if (isIOSStandaloneCapable()) triggerInstall();
  });

  closeBtn.addEventListener("click", () => {
    dismissForDays();
    hideToast();
  });

  actions.appendChild(installBtn);
  actions.appendChild(closeBtn);
  toastEl.appendChild(content);
  toastEl.appendChild(actions);
  document.body.appendChild(toastEl);

  return toastEl;
}

export function canInstall() {
  if (isStandalone()) return false;
  return !!installPromptEvent || isIOSStandaloneCapable();
}

export async function triggerInstall() {
  if (installPromptEvent) {
    installPromptEvent.prompt();
    await installPromptEvent.userChoice;
    installPromptEvent = null;
    return;
  }
  if (isIOSStandaloneCapable()) {
    // No hay API programática en iOS; mostrar instrucciones (flujo real de Safari)
    const { default: Swal } = await import("sweetalert2");
    Swal.fire({
      icon: "info",
      title: "Instalar en iPhone",
      html: `<ol style="text-align:left;line-height:1.9;margin:0;padding-left:1.2rem">
          <li>Tocá el menú <strong><i class='fas fa-ellipsis'></i></strong> abajo a la derecha.</li>
          <li>Elegí <strong>Compartir</strong> <i class='fas fa-arrow-up-from-bracket'></i>.</li>
          <li>Tocá <strong>Ver más</strong> <i class='fas fa-chevron-down'></i> si no ves la opción.</li>
          <li>Elegí <strong>Agregar a Inicio</strong> <i class='fas fa-plus'></i>.</li>
        </ol>`,
      confirmButtonText: "Entendido",
    });
  }
}

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  installPromptEvent = event;
  showToast();
});

window.addEventListener("appinstalled", () => {
  installPromptEvent = null;
  removeToast();
});

document.addEventListener("DOMContentLoaded", () => {
  if (isStandalone()) return;
  createToast();
  if (isIOSStandaloneCapable() && !isDismissed()) {
    showToast();
  }
});
